import React from "react";
import {
  canManageAnySourceFile,
  canManageSourceField,
} from "../lib/accessControl.js";
import {
  deleteCurrentSourceFile,
  downloadCurrentSourceFile,
  getSourceFilesStorageRuntimeInfo,
  isSourceFilesStorageConfigured,
  resolveCurrentSourceFile,
  resolveSourceStoragePathLabel,
  uploadCurrentSourceFile,
} from "../lib/sourceFilesStorage.js";
import useAccess from "./useAccess.jsx";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function buildInitialSourceState(field) {
  return {
    currentFile: null,
    errorMessage: "",
    feedback: null,
    field,
    isDeleting: false,
    isDownloading: false,
    isLoading: false,
    isUploading: false,
    pathLabel: resolveSourceStoragePathLabel(field),
  };
}

function buildInitialSourceStates(fields) {
  return fields.reduce((state, field) => ({
    ...state,
    [field]: buildInitialSourceState(field),
  }), {});
}

function updateSourceState(state, field, nextPartialState) {
  const currentFieldState = isPlainObject(state?.[field])
    ? state[field]
    : buildInitialSourceState(field);

  return {
    ...state,
    [field]: {
      ...currentFieldState,
      ...nextPartialState,
    },
  };
}

function buildFeedback(message, tone) {
  return {
    message,
    tone,
  };
}

function resolveStorageErrorMessage(error, fallbackMessage) {
  const statusCode = typeof error?.status === "number"
    ? error.status
    : typeof error?.statusCode === "string" && error.statusCode.trim()
      ? Number(error.statusCode)
      : null;
  const message = typeof error?.message === "string"
    ? error.message.trim()
    : "";

  if (statusCode === 401) {
    return "Le stockage des fichiers courants a refusé cette action pour la session courante.";
  }

  if (statusCode === 403) {
    return message || "Le stockage des fichiers courants a refusé cette action pour la session courante.";
  }

  if (statusCode === 404) {
    return "Le stockage demandé n'existe pas encore ou n'est pas accessible.";
  }

  return message || fallbackMessage;
}

export default function useSourceFilesStorage(sourceFields) {
  const { access, isReady: isAccessReady } = useAccess();
  const normalizedSourceFields = React.useMemo(() => (
    Array.isArray(sourceFields)
      ? sourceFields.filter((field) => typeof field === "string" && field.trim())
      : []
  ), [sourceFields]);
  const sourceFieldsKey = React.useMemo(() => normalizedSourceFields.join("|"), [normalizedSourceFields]);
  const [sourceStatesByField, setSourceStatesByField] = React.useState(() => buildInitialSourceStates(normalizedSourceFields));
  const [storageRuntimeInfo, setStorageRuntimeInfo] = React.useState(() => getSourceFilesStorageRuntimeInfo());
  const sourceStatesRef = React.useRef(sourceStatesByField);

  React.useEffect(() => {
    sourceStatesRef.current = sourceStatesByField;
  }, [sourceStatesByField]);

  React.useEffect(() => {
    setSourceStatesByField(buildInitialSourceStates(normalizedSourceFields));
  }, [sourceFieldsKey, normalizedSourceFields]);

  const storageAccess = React.useMemo(() => {
    const canManageSourceFilesByField = normalizedSourceFields.reduce((nextState, field) => {
      nextState[field] = canManageSourceField(access, field);
      return nextState;
    }, {});

    if (!isSourceFilesStorageConfigured()) {
      return {
        canManageAnySourceFile: false,
        canManageSourceFilesByField,
        isAvailable: false,
        reason: "Le stockage des fichiers courants n'est pas configuré dans ce frontend.",
      };
    }

    return {
      canManageAnySourceFile: canManageAnySourceFile(access),
      canManageSourceFilesByField,
      isAvailable: true,
      reason: "",
    };
  }, [access, normalizedSourceFields]);
  const accessKey = React.useMemo(() => `${access.userId}|${access.permissions.join("|")}`, [access]);
  const refreshStorageRuntimeInfo = React.useCallback(() => {
    setStorageRuntimeInfo(getSourceFilesStorageRuntimeInfo());
  }, []);

  const setSourceState = React.useCallback((field, nextPartialState) => {
    setSourceStatesByField((currentState) => updateSourceState(currentState, field, nextPartialState));
  }, []);

  React.useEffect(() => {
    if (!isAccessReady) {
      return undefined;
    }

    let isCancelled = false;

    if (!storageAccess.isAvailable) {
      refreshStorageRuntimeInfo();
      setSourceStatesByField((currentState) => normalizedSourceFields.reduce((nextState, field) => updateSourceState(nextState, field, {
        currentFile: null,
        errorMessage: "",
        feedback: null,
        isDeleting: false,
        isDownloading: false,
        isLoading: false,
        isUploading: false,
      }), currentState));
      return undefined;
    }

    setSourceStatesByField((currentState) => normalizedSourceFields.reduce((nextState, field) => updateSourceState(nextState, field, {
      errorMessage: "",
      isLoading: true,
    }), currentState));

    void Promise.all(normalizedSourceFields.map(async (field) => {
      try {
        const { currentFile } = await resolveCurrentSourceFile(field);
        return { currentFile, errorMessage: "", field };
      } catch (error) {
        return {
          currentFile: null,
          errorMessage: resolveStorageErrorMessage(error, "Le fichier courant n'a pas pu être résolu."),
          field,
        };
      }
    })).then((results) => {
      if (isCancelled) {
        return;
      }

      setSourceStatesByField((currentState) => results.reduce((nextState, result) => updateSourceState(nextState, result.field, {
        currentFile: result.currentFile,
        errorMessage: result.errorMessage,
        isLoading: false,
      }), currentState));
      refreshStorageRuntimeInfo();
    });

    return () => {
      isCancelled = true;
    };
  }, [
    accessKey,
    isAccessReady,
    normalizedSourceFields,
    refreshStorageRuntimeInfo,
    sourceFieldsKey,
    storageAccess.isAvailable,
  ]);

  const handleDownloadSourceFile = React.useCallback(async (field) => {
    if (!storageAccess.isAvailable) {
      setSourceState(field, {
        feedback: buildFeedback(storageAccess.reason, "warning"),
      });
      return;
    }

    const currentFieldState = sourceStatesRef.current[field] || buildInitialSourceState(field);
    if (!currentFieldState.currentFile) {
      setSourceState(field, {
        feedback: buildFeedback("Aucun fichier courant n'est disponible pour cette source.", "warning"),
      });
      return;
    }

    setSourceState(field, {
      errorMessage: "",
      feedback: null,
      isDownloading: true,
    });

    try {
      await downloadCurrentSourceFile(field, currentFieldState.currentFile);
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        feedback: buildFeedback("Le téléchargement du fichier courant a été lancé.", "success"),
        isDownloading: false,
      });
    } catch (error) {
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        feedback: buildFeedback(resolveStorageErrorMessage(error, "Le téléchargement du fichier courant a échoué."), "danger"),
        isDownloading: false,
      });
    }
  }, [refreshStorageRuntimeInfo, setSourceState, storageAccess.isAvailable, storageAccess.reason]);

  const handleUploadSourceFile = React.useCallback(async (field, file) => {
    if (!storageAccess.isAvailable) {
      setSourceState(field, {
        feedback: buildFeedback(storageAccess.reason, "warning"),
      });
      return;
    }

    if (storageAccess.canManageSourceFilesByField[field] !== true) {
      setSourceState(field, {
        feedback: buildFeedback("Cette source ne peut pas être modifiée avec le rôle actif.", "warning"),
      });
      return;
    }

    const hadCurrentFile = Boolean(sourceStatesRef.current[field]?.currentFile);
    setSourceState(field, {
      errorMessage: "",
      feedback: null,
      isUploading: true,
    });

    try {
      await uploadCurrentSourceFile(field, file);
      const { currentFile } = await resolveCurrentSourceFile(field);
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        currentFile,
        feedback: buildFeedback(
          hadCurrentFile
            ? "Le fichier courant a été remplacé dans le stockage courant."
            : "Le fichier courant a été déposé dans le stockage courant.",
          "success",
        ),
        isLoading: false,
        isUploading: false,
      });
    } catch (error) {
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        feedback: buildFeedback(resolveStorageErrorMessage(error, "Le dépôt du fichier courant a échoué."), "danger"),
        isUploading: false,
      });
    }
  }, [
    refreshStorageRuntimeInfo,
    setSourceState,
    storageAccess.canManageSourceFilesByField,
    storageAccess.isAvailable,
    storageAccess.reason,
  ]);

  const handleDeleteSourceFile = React.useCallback(async (field) => {
    if (!storageAccess.isAvailable) {
      setSourceState(field, {
        feedback: buildFeedback(storageAccess.reason, "warning"),
      });
      return;
    }

    if (storageAccess.canManageSourceFilesByField[field] !== true) {
      setSourceState(field, {
        feedback: buildFeedback("Cette source ne peut pas être modifiée avec le rôle actif.", "warning"),
      });
      return;
    }

    setSourceState(field, {
      errorMessage: "",
      feedback: null,
      isDeleting: true,
    });

    try {
      await deleteCurrentSourceFile(field);
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        currentFile: null,
        feedback: buildFeedback("Le fichier courant a été supprimé du stockage courant.", "success"),
        isDeleting: false,
      });
    } catch (error) {
      refreshStorageRuntimeInfo();
      setSourceState(field, {
        feedback: buildFeedback(resolveStorageErrorMessage(error, "La suppression du fichier courant a échoué."), "danger"),
        isDeleting: false,
      });
    }
  }, [
    refreshStorageRuntimeInfo,
    setSourceState,
    storageAccess.canManageSourceFilesByField,
    storageAccess.isAvailable,
    storageAccess.reason,
  ]);

  return {
    handleDeleteSourceFile,
    handleDownloadSourceFile,
    handleUploadSourceFile,
    isSessionReady: isAccessReady,
    sourceStatesByField,
    storageAccess,
    storageRuntimeInfo,
  };
}
